import React, { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { BrandProvider } from "@/lib/brand";
import Login from "@/pages/Login";
import AdminDashboard from "@/pages/AdminDashboard";
import InstructorDashboard from "@/pages/InstructorDashboard";
import StudentDashboard from "@/pages/StudentDashboard";
import StudioManagement from "@/pages/StudioManagement";
import StudioSettings from "@/pages/StudioSettings";
import UserManagement from "@/pages/UserManagement";
import AuditLog from "@/pages/AuditLog";
import DanceStylesManagement from "@/pages/DanceStylesManagement";
import ClassManagement from "@/pages/ClassManagement";
import StudentManagement from "@/pages/StudentManagement";
import AttendanceManagement from "@/pages/AttendanceManagement";
import PaymentManagement from "@/pages/PaymentManagement";
import AnnouncementManagement from "@/pages/AnnouncementManagement";
import StudioCalendar from "@/pages/StudioCalendar";
import InstructorManagement from "@/pages/InstructorManagement";
import InstructorPayroll from "@/pages/InstructorPayroll";
import LedgerManagement from "@/pages/LedgerManagement";
import CustomerManagement from "@/pages/CustomerManagement";
import StudentOnboarding from "@/components/StudentOnboarding";
import ReceiptView from "@/pages/ReceiptView";
import PickupQrLanding from "@/pages/PickupQrLanding";
import PickupPage from "@/pages/PickupPage";
import PublicJoinPage from "@/pages/PublicJoinPage";
import JoinLinksManagement from "@/pages/JoinLinksManagement";
import { Toaster } from "@/components/ui/sonner";
import { MobileTopBar } from "@/components/Navigation";

function App() {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem("user");
    return stored ? JSON.parse(stored) : null;
  });
  const [token, setToken] = useState(localStorage.getItem("token"));

  const handleLogin = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("token", authToken);
  };

  const handleLogout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("active_studio_id");
    localStorage.removeItem("active_studio");
  };

  const handleOnboardingComplete = (updatedUser) => {
    setUser(updatedUser);
  };

  if (!user || !token) {
    return (
      <BrandProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/r/:shortcode/:token" element={<ReceiptView />} />
            <Route path="/p/:token" element={<PickupQrLanding />} />
            <Route path="/join/:token" element={<PublicJoinPage />} />
            <Route path="/" element={<Login onLogin={handleLogin} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </BrandProvider>
    );
  }

  // Check if student needs onboarding
  if (user.role === "student" && !user.profile_completed) {
    return (
      <BrandProvider>
        <StudentOnboarding user={user} onComplete={handleOnboardingComplete} />
        <Toaster position="top-center" richColors />
      </BrandProvider>
    );
  }

  const isAdminRole = user.role === "admin" || user.role === "super_admin";

  return (
    <BrandProvider>
      <BrowserRouter>
        <MobileTopBar />
        <Routes>
          <Route path="/r/:shortcode/:token" element={<ReceiptView />} />
          <Route path="/p/:token" element={<PickupQrLanding />} />
          <Route path="/join/:token" element={<PublicJoinPage />} />
          {isAdminRole && (
            <>
              <Route path="/" element={<AdminDashboard user={user} onLogout={handleLogout} />} />
              <Route path="/studios" element={<StudioManagement user={user} onLogout={handleLogout} />} />
              <Route path="/studio-settings" element={<StudioSettings user={user} onLogout={handleLogout} />} />
              <Route path="/users" element={<UserManagement user={user} onLogout={handleLogout} />} />
              <Route path="/dance-styles" element={<DanceStylesManagement user={user} onLogout={handleLogout} />} />
              <Route path="/classes" element={<ClassManagement user={user} onLogout={handleLogout} />} />
              <Route path="/students" element={<StudentManagement user={user} onLogout={handleLogout} />} />
              <Route path="/attendance" element={<AttendanceManagement user={user} onLogout={handleLogout} />} />
              <Route path="/payments" element={<PaymentManagement user={user} onLogout={handleLogout} />} />
              <Route path="/announcements" element={<AnnouncementManagement user={user} onLogout={handleLogout} />} />
              <Route path="/calendar" element={<StudioCalendar user={user} onLogout={handleLogout} />} />
              <Route path="/instructors" element={<InstructorManagement user={user} onLogout={handleLogout} />} />
              <Route path="/payroll" element={<InstructorPayroll user={user} onLogout={handleLogout} />} />
              <Route path="/ledger" element={<LedgerManagement user={user} onLogout={handleLogout} />} />
              <Route path="/customers" element={<CustomerManagement user={user} onLogout={handleLogout} />} />
              <Route path="/pickup" element={<PickupPage user={user} onLogout={handleLogout} />} />
              <Route path="/join-links" element={<JoinLinksManagement user={user} onLogout={handleLogout} />} />
              <Route path="/audit-log" element={<AuditLog user={user} onLogout={handleLogout} />} />
            </>
          )}
          {user.role === "instructor" && (
            <>
              <Route path="/" element={<InstructorDashboard user={user} onLogout={handleLogout} />} />
              <Route path="/attendance" element={<AttendanceManagement user={user} onLogout={handleLogout} />} />
              <Route path="/calendar" element={<StudioCalendar user={user} onLogout={handleLogout} />} />
              <Route path="/payments" element={<PaymentManagement user={user} onLogout={handleLogout} />} />
              <Route path="/students" element={<StudentManagement user={user} onLogout={handleLogout} />} />
              <Route path="/pickup" element={<PickupPage user={user} onLogout={handleLogout} />} />
            </>
          )}
          {user.role === "student" && (
            <>
              <Route path="/" element={<StudentDashboard user={user} onLogout={handleLogout} />} />
            </>
          )}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </BrandProvider>
  );
}

export default App;
